import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, UserPlus, ClipboardCheck, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { icon: UserPlus, title: "Send a friend our way", desc: "Share their name and number below — we'll take it from there." },
  { icon: ClipboardCheck, title: "They book & we deliver", desc: "When their job is completed and paid, your referral counts." },
  { icon: Gift, title: "You get $25 on Amazon", desc: "A $25 Amazon gift card lands in your hands. No limit on referrals." },
];

const EMPTY = { referrer_name: "", referrer_phone: "", referee_name: "", referee_phone: "", notes: "" };

const inputCls =
  "w-full rounded-xl border border-white/15 bg-ink px-4 py-3 text-sm text-slate-100 placeholder-slate-500 transition-colors focus:border-orange-500 focus:outline-none";

export const Referral = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/referrals`, form);
      toast.success("Referral received — thank you! We'll reach out to them shortly.");
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="referrals" className="relative overflow-hidden border-t border-white/10 py-20 lg:py-32" data-testid="referral-section">
      <div className="pointer-events-none absolute -left-32 top-1/3 h-[420px] w-[420px] rounded-full bg-orange-500/10 blur-[140px]" />
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <Reveal>
          <div className="flex items-center gap-4">
            <span className="h-px w-10 bg-orange-500" />
            <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-orange-500">
              Refer &amp; Earn
            </p>
          </div>
          <h2 className="mt-4 max-w-4xl font-display text-4xl font-extrabold uppercase leading-[0.95] text-white sm:text-5xl lg:text-6xl">
            Refer a neighbor.{" "}
            <span className="font-serif normal-case italic tracking-normal text-orange-500">
              earn a $25 Amazon gift card
            </span>
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
            Know someone with overflowing gutters or a damp basement? For every
            referral that turns into a sold job, we'll send you a{" "}
            <strong className="text-slate-200">$25 Amazon gift card</strong> as a
            thank-you.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div className="space-y-4">
            {STEPS.map((step, i) => (
              <Reveal key={step.title} delay={i * 0.1}>
                <div
                  className="flex items-start gap-5 rounded-2xl border border-white/10 bg-ink2 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-orange-500/40"
                  data-testid={`referral-step-${i + 1}`}
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-orange-500/10 ring-1 ring-orange-500/30">
                    <step.icon className="h-6 w-6 text-orange-500" />
                  </span>
                  <div>
                    <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-slate-500">
                      Step <span className="text-orange-500">/ 0{i + 1}</span>
                    </p>
                    <h3 className="mt-1 font-display text-xl font-bold uppercase text-white sm:text-2xl">
                      {step.title}
                    </h3>
                    <p className="mt-1 text-sm text-slate-400">{step.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <form
              onSubmit={submit}
              className="rounded-2xl border border-white/10 bg-ink p-6 sm:p-8"
              data-testid="referral-form"
            >
              <h3 className="font-display text-2xl font-bold uppercase text-white">
                Drop their details
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                We'll introduce ourselves and mention you sent us.
              </p>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="ref-your-name" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-400">
                    Your Name
                  </label>
                  <input id="ref-your-name" required value={form.referrer_name} onChange={set("referrer_name")} placeholder="Jane Rivera" className={inputCls} data-testid="referral-input-referrer-name" />
                </div>
                <div>
                  <label htmlFor="ref-your-phone" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-400">
                    Your Phone
                  </label>
                  <input id="ref-your-phone" type="tel" required value={form.referrer_phone} onChange={set("referrer_phone")} placeholder="(609) 555-0100" className={inputCls} data-testid="referral-input-referrer-phone" />
                </div>
                <div>
                  <label htmlFor="ref-friend-name" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-400">
                    Friend's Name
                  </label>
                  <input id="ref-friend-name" required value={form.referee_name} onChange={set("referee_name")} placeholder="Mike Smith" className={inputCls} data-testid="referral-input-referee-name" />
                </div>
                <div>
                  <label htmlFor="ref-friend-phone" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-400">
                    Friend's Phone
                  </label>
                  <input id="ref-friend-phone" type="tel" required value={form.referee_phone} onChange={set("referee_phone")} placeholder="(302) 555-0100" className={inputCls} data-testid="referral-input-referee-phone" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="ref-notes" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-400">
                    Anything We Should Know?
                  </label>
                  <textarea id="ref-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder="e.g. Their gutters overflow every storm…" className={inputCls} data-testid="referral-input-notes" />
                </div>
              </div>
              <button
                type="submit"
                disabled={sending}
                className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-orange-500 px-7 py-4 text-sm font-bold uppercase tracking-wide text-ink transition-all hover:-translate-y-0.5 hover:bg-orange-600 hover:text-white disabled:opacity-60"
                data-testid="referral-submit-button"
              >
                {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                {sending ? "Sending…" : "Send My Referral"}
              </button>
              <p className="mt-4 text-center text-xs text-slate-500">
                Gift card issued once the referred job is completed and paid.
                Unlimited referrals — every sold job earns another $25.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
